/**
 * Secure Merchant Authentication Service
 * Industry-standard authentication with proper security measures
 */

import axios from '../axiosConfig';

class MerchantAuthService {
  constructor() {
    this.isRefreshing = false;
    this.failedQueue = [];
    this.setupAxios();
  }

  /**
   * Setup axios with interceptors
   */
  setupAxios() {
    // Use the shared axiosConfig instance
    this.axios = axios;
  }


  /**
   * Login with credentials
   */
  async login(email, password) {
    try {
      const response = await this.axios.post('merchant/auth/login', {
        email,
        password,
      });

      // Token is stored in HTTP-only cookie by backend - DO NOT store in localStorage
      // This is a security best practice to prevent XSS attacks

      return {
        success: true,
        data: {
          token: response.data.token, // Only used for response, not stored
          userId: response.data.userId,
          role: response.data.role,
        },
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Login failed',
      };
    }
  }

  /**
   * Register new merchant
   */
  async register(userData) {
    try {
      const response = await this.axios.post('merchant/auth/register', userData);

      // Token is stored in HTTP-only cookie by backend - DO NOT store in localStorage
      // This is a security best practice to prevent XSS attacks

      return {
        success: true,
        data: {
          token: response.data.token, // Only used for response, not stored
          userId: response.data.userId,
          role: response.data.role,
        },
      };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Registration failed',
      };
    }
  }


  /**
   * Get current user profile
   */
  async getCurrentUser() {
    try {
      console.log('AuthService: Getting current user...');
      const response = await this.axios.get('merchant/auth/profile');
      console.log('AuthService: getCurrentUser response:', response.data);
      // Backend returns { success: true, user: {...} }, so extract just the user object
      return response.data.user || response.data;
    } catch (error) {
      console.log('AuthService: getCurrentUser error:', error.response?.status, error.message);
      if (error.response?.status === 401) {
        return null; // Not authenticated
      }
      console.error('Get current user error:', error);
      return null;
    }
  }

  /**
   * Logout user
   */
  async logout() {
    try {
      await this.axios.post('merchant/auth/logout');
      // Backend will clear the HTTP-only cookie
      // No need to clear localStorage since we don't store tokens there
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  /**
   * Check if user is authenticated
   */
  async checkAuth() {
    try {
      console.log('AuthService: Checking authentication...');
      const userData = await this.getCurrentUser();
      console.log('AuthService: getCurrentUser result:', userData);
      return userData ? { isAuthenticated: true, user: userData } : { isAuthenticated: false, user: null };
    } catch (error) {
      console.error('AuthService: Check auth error:', error);
      return { isAuthenticated: false, user: null };
    }
  }

  /**
   * Get axios instance for other API calls
   */
  getAxios() {
    return this.axios;
  }
}

export default new MerchantAuthService();
